import axios from 'axios'
import config from '../../../config'
import { AddressBlock, PhoneValue, VendorOnboardingForm } from '../interfaces'

export class FinanceOnboardingConfigError extends Error {}

const formatPhone = (phone: PhoneValue): string =>
    phone.number ? `${phone.dial} ${phone.number}` : ''

/**
 * The entire wire contract lives here. n8n's `Parse Submission` expects the flat,
 * human-labelled keys the original n8n Form Trigger produced, so the web payload
 * is shaped to match exactly — that's what lets both entry points share the same
 * downstream chain.
 */
const addressKeys = (prefix: 'Billing' | 'Shipping', a: AddressBlock) => ({
    [`${prefix} Attention`]: a.attention,
    [`${prefix} Country/Region`]: a.country,
    [`${prefix} Street 1`]: a.street1,
    [`${prefix} Street 2`]: a.street2,
    [`${prefix} City`]: a.city,
    [`${prefix} State`]: a.state,
    [`${prefix} Pin Code`]: a.pinCode,
    [`${prefix} Phone`]: formatPhone(a.phone),
    // No fax key: n8n's Parse Submission reads it as `j['... Fax Number'] || ''`,
    // so omitting it is safe and the legacy form keeps its own fax fields.
})

export const toWirePayload = (values: VendorOnboardingForm) => {
    const shipping = values.sameAsBilling ? values.billing : values.shipping

    const contactKeys = values.contacts.reduce<Record<string, unknown>>(
        (acc, contact, index) => {
            const n = index + 1
            acc[`Contact ${n} First Name`] = contact.firstName
            acc[`Contact ${n} Last Name`] = contact.lastName
            acc[`Contact ${n} Email`] = contact.email
            acc[`Contact ${n} Phone`] = formatPhone(contact.phone)
            // Checkbox fields arrive from the n8n form as arrays; match that.
            acc[`Send Invoice to Contact ${n}`] = contact.sendInvoice ? ['Yes'] : []
            return acc
        },
        {}
    )

    return {
        'Company Name': values.companyName,
        'PAN Number': values.panNumber,
        'Signing Authority Name': values.signingAuthorityName,
        'Signing Authority Email': values.signingAuthorityEmail,
        'GST Number': values.gstNumber,
        ...addressKeys('Billing', values.billing),
        'Same as Billing Address': values.sameAsBilling
            ? ['Yes, use billing address']
            : [],
        ...addressKeys('Shipping', shipping),
        ...contactKeys,
    }
}

interface SubmitArgs {
    values: VendorOnboardingForm
    recaptchaToken: string
    renderedAt: number
}

export const submitVendorOnboarding = async ({
    values,
    recaptchaToken,
    renderedAt,
}: SubmitArgs): Promise<{ referenceId: string }> => {
    const settings = config.financeOnboarding

    if (!settings?.webhookUrl) {
        throw new FinanceOnboardingConfigError(
            'This form is not configured for the current environment.'
        )
    }

    if (!values.gstCertificate) {
        throw new Error('Please attach your GST certificate before submitting.')
    }

    const formData = new FormData()
    formData.append(
        'payload',
        JSON.stringify({
            ...toWirePayload(values),
            submitToken: settings.submitToken,
            recaptchaToken,
            website: values.website,
            _renderedAt: renderedAt,
            submitterEmail: values.submitterEmail,
        })
    )
    formData.append('gstCertificate', values.gstCertificate)

    // Bare axios on purpose. The global interceptors in src/contexts/Auth.tsx attach
    // a Keycloak bearer token and can call redirectToLogin() on a 401 — which would
    // bounce an anonymous vendor to the login page. Content-Type is deliberately
    // unset so the browser adds the multipart boundary and the request stays a
    // CORS-simple one (no preflight).
    const { data } = await axios.post(settings.webhookUrl, formData, {
        timeout: 60_000,
    })

    // n8n answers a workflow error with HTTP 200 and an EMPTY body rather than a 4xx,
    // so status alone cannot be trusted. Treat anything without an explicit ok+
    // referenceId as a rejection — otherwise a failed reCAPTCHA check would render
    // the success screen with a blank reference ID and the vendor would walk away
    // believing they had submitted.
    if (!data || data.ok !== true || !data.referenceId) {
        throw new Error(
            'The submission was not accepted. This is usually a failed security check — please reload the page and try again.'
        )
    }

    return { referenceId: data.referenceId }
}
